import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import RoleSwitcher from '../components/RoleSwitcher';
import { apiCall } from '../utils/api';
import { SecureFileLink } from '../components/hris/SecureFileLink';
import { compressImage } from '../utils/imageCompress';
import { toast } from '../components/ui/sonner';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import { 
  Users, Briefcase, DollarSign, Settings, CheckCircle, XCircle, TrendingUp,
  Shield, UserCheck, UserX, BarChart3, Activity, PieChart, Clock,
  Building2, LogOut, RefreshCw, ChevronRight, AlertTriangle, Percent,
  Mail, Phone, MapPin, Calendar, Award, Star, Eye, X, Wallet, FileText,
  FileCheck, Download, ExternalLink, Headphones, MessageSquare, Send,
  AlertCircle, User, ChevronDown, Store, Archive, ArchiveRestore, Trash2, Search, UserPlus, ShieldCheck, Sparkles, BookOpen
} from 'lucide-react';
import AdminMarketplacePanel from '../components/admin/AdminMarketplacePanel';
import AdminReportRecipientsPanel from '../components/admin/AdminReportRecipientsPanel';
import AdminSupportModePanel from '../components/admin/AdminSupportModePanel';
import OrgHrisConsole from '../components/hris/OrgHrisConsole';
import AuditTrailPanel from '../components/admin/AuditTrailPanel';
import AdminJobsPanel from '../components/admin/AdminJobsPanel';
import DataQualityPanel from '../components/admin/DataQualityPanel';
import MinPaySettingsPanel from '../components/admin/MinPaySettingsPanel';
import SpinRewardsStudio from '../components/admin/SpinRewardsStudio';
import SecurityConfigPanel from '../components/admin/SecurityConfigPanel';
import SecurityCenter from '../components/security/SecurityCenter';
import AdminProfileVerificationPanel from '../components/admin/AdminProfileVerificationPanel';
import BackgroundChecks from '../components/admin/BackgroundChecks';
import StreakRewardsPanel from '../components/org/StreakRewardsPanel';
import SettlementsPanel from '../components/admin/SettlementsPanel';
import UserManagementPanel from '../components/UserManagementPanel';
import UserActivityDrawer from '../components/UserActivityDrawer';
import RecentAutoLockoutsCard from '../components/admin/RecentAutoLockoutsCard';
import DataQualityOverviewCard from '../components/admin/DataQualityOverviewCard';
import PlatformFinanceCard from '../components/admin/PlatformFinanceCard';
import RevenueRulesPanel from '../components/admin/RevenueRulesPanel';
import MonetizationPanel from '../components/admin/MonetizationPanel';
import FinancialCommandCenter from '../components/admin/FinancialCommandCenter';
import EnterpriseCommandCenter from '../components/admin/EnterpriseCommandCenter';
import ConfigurationCenter from '../components/admin/ConfigurationCenter';
import SecurityMonitorPanel from '../components/admin/SecurityMonitorPanel';
import HelpTraining from '../components/shared/HelpTraining';
import OnboardingCard from '../components/shared/OnboardingCard';
import ScensobConnectLogo from '../components/brand/ScensobConnectLogo';
import PaymentDisputes from '../components/admin/PaymentDisputes';

export default function AdminDashboard() {
  const { user, logout, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('stats');
  const [settleAttn, setSettleAttn] = useState(0);

  useEffect(() => {
    const loadAttn = async () => {
      try {
        const r = await apiCall('/api/admin/finance/settlements/attention');
        setSettleAttn(r.attention || 0);
      } catch {}
    };
    loadAttn();
    const iv = setInterval(loadAttn, 60000);
    return () => clearInterval(iv);
  }, []);
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [commission, setCommission] = useState({
    worker_pct: 5.0,
    employer_pct: 3.0,
    platform_fixed: 0.50,
  });
  const [loading, setLoading] = useState(false);
  
  // Certificate verification states
  const [pendingCertificates, setPendingCertificates] = useState([]);
  const [certLoading, setCertLoading] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [verifyingCert, setVerifyingCert] = useState(null);
  
  // User detail modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  
  // User filter for All Users tab
  const [userFilter, setUserFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('all'); // all | verified | pending | archived
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(null); // { count, typed }
  const [bulkBusy, setBulkBusy] = useState(false);
  
  // Support tickets states
  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState(null);
  const [ticketFilter, setTicketFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [respondText, setRespondText] = useState('');
  const [noteText, setNoteText] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    loadStats();
    loadPendingUsers();
    loadCommission();
    loadAllUsers();
    loadPendingCertificates();
    loadSupportTickets();
    loadTicketStats();
  }, []);

  // Live sync: refresh admin data whenever any relevant record changes anywhere.
  useLiveRefresh(['users', 'jobs', 'credentials', 'tickets', 'programs', 'verification', 'approvals', 'agencies', 'payments'], () => {
    loadStats();
    loadPendingUsers();
    loadAllUsers();
    loadPendingCertificates();
    loadSupportTickets();
    loadTicketStats();
  }, { debounceMs: 1200 });

  const loadStats = async () => {
    try {
      const response = await apiCall('/api/admin/stats');
      setStats(response.stats);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const loadPendingUsers = async () => {
    try {
      const response = await apiCall('/api/admin/users/pending');
      setPendingUsers(response.users || []);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const loadAllUsers = async () => {
    try {
      const response = await apiCall('/api/admin/users');
      setAllUsers(response.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadPendingCertificates = async () => {
    setCertLoading(true);
    try {
      const response = await apiCall('/api/admin/certificates/pending');
      setPendingCertificates(response.certificates || []);
    } catch (error) {
      console.error('Failed to load pending certificates:', error);
    } finally {
      setCertLoading(false);
    }
  };

  const loadSupportTickets = async () => {
    setTicketLoading(true);
    try {
      const response = await apiCall('/api/support/tickets');
      setSupportTickets(response.tickets || []);
    } catch (error) {
      console.error('Failed to load support tickets:', error);
    } finally {
      setTicketLoading(false);
    }
  };

  const loadTicketStats = async () => {
    try {
      const response = await apiCall('/api/admin/support/stats');
      setTicketStats(response.stats);
    } catch (error) {
      console.error('Failed to load ticket stats:', error);
    }
  };

  const loadTicketDetail = async (ticketId) => {
    try {
      const response = await apiCall(`/api/support/tickets/${ticketId}`);
      setSelectedTicket(response.ticket);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await apiCall(`/api/support/tickets/${ticketId}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
      toast.success(`Ticket status updated to ${status}`);
      loadSupportTickets();
      loadTicketStats();
      if (selectedTicket?._id === ticketId) {
        loadTicketDetail(ticketId);
      }
    } catch (error) {
      toast.error('Failed to update ticket status');
    }
  };

  const updateTicketPriority = async (ticketId, priority) => {
    try {
      await apiCall(`/api/support/tickets/${ticketId}`, {
        method: 'PUT',
        body: JSON.stringify({ priority })
      });
      toast.success(`Priority updated to ${priority}`);
      loadSupportTickets();
      loadTicketStats();
      if (selectedTicket?._id === ticketId) {
        loadTicketDetail(ticketId);
      }
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const sendTicketResponse = async () => {
    if (!respondText.trim() || !selectedTicket) return;
    setSendingResponse(true);
    try {
      await apiCall(`/api/support/tickets/${selectedTicket._id}/respond`, {
        method: 'POST',
        body: JSON.stringify({ message: respondText, status: 'in_progress' })
      });
      toast.success('Response sent to user');
      setRespondText('');
      loadTicketDetail(selectedTicket._id);
      loadSupportTickets();
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setSendingResponse(false);
    }
  };

  const addInternalNote = async () => {
    if (!noteText.trim() || !selectedTicket) return;
    setAddingNote(true);
    try {
      await apiCall(`/api/support/tickets/${selectedTicket._id}/notes`, {
        method: 'POST',
        body: JSON.stringify({ note: noteText })
      });
      toast.success('Note added');
      setNoteText('');
      loadTicketDetail(selectedTicket._id);
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleVerifyCertificate = async (certId, action, notes = '') => {
    setVerifyingCert(certId);
    try {
      await apiCall(`/api/admin/certificates/${certId}/verify`, {
        method: 'POST',
        body: JSON.stringify({ action, notes })
      });
      toast.success(`Certificate ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      loadPendingCertificates();
    } catch (error) {
      toast.error(error.message || 'Failed to verify certificate');
    } finally {
      setVerifyingCert(null);
      setSelectedCert(null);
    }
  };

  const loadCommission = async () => {
    try {
      const response = await apiCall('/api/admin/settings/commission');
      setCommission(response.commission);
    } catch (error) {
      console.error('Failed to load commission:', error);
    }
  };

  const loadUserDetail = async (userId) => {
    setUserDetailLoading(true);
    try {
      const response = await apiCall(`/api/admin/users/${userId}`);
      setUserDetail(response);
    } catch (error) {
      toast.error('Failed to load user details');
    } finally {
      setUserDetailLoading(false);
    }
  };

  const handleViewUser = (u) => {
    setSelectedUser(u);
    loadUserDetail(u._id);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
    setUserDetail(null);
  };

  const handleVerifyUser = async (userId, action) => {
    try {
      await apiCall('/api/admin/users/verify', {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          action: action,
        }),
      });
      toast.success(`User ${action}d successfully`);
      loadPendingUsers();
      loadStats();
      loadAllUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateCommission = async (e) => {
    e.preventDefault();
    try {
      await apiCall('/api/admin/settings/commission', {
        method: 'PUT',
        body: JSON.stringify(commission),
      });
      toast.success('Commission settings updated');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Filter users: role tab + search + status (verified/pending/archived)
  const getFilteredUsers = () => {
    const q = (userSearch || '').trim().toLowerCase();
    return allUsers.filter(u => {
      if (userFilter !== 'all' && u.role !== userFilter) return false;
      if (userStatusFilter === 'verified' && !(u.is_admin_verified || u.role === 'worker' || u.role === 'admin') ) return false;
      if (userStatusFilter === 'pending' && (u.is_admin_verified || u.role === 'worker' || u.role === 'admin')) return false;
      if (userStatusFilter === 'archived' && !u.is_archived) return false;
      if (userStatusFilter !== 'archived' && u.is_archived && userStatusFilter !== 'all') return false;
      if (q) {
        const hay = `${u.full_name || ''} ${u.email || ''} ${u.phone || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  };

  // ---------- Bulk archive / delete helpers ----------
  const toggleSelect = (id) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (e) => {
    const visible = getFilteredUsers().filter(u => u.role !== 'admin').map(u => u._id);
    if (e.target.checked) {
      setSelectedUserIds(new Set(visible));
    } else {
      setSelectedUserIds(new Set());
    }
  };

  const clearSelection = () => setSelectedUserIds(new Set());

  const bulkArchive = async () => {
    const ids = Array.from(selectedUserIds);
    if (!ids.length) return;
    if (!window.confirm(`Archive ${ids.length} user${ids.length !== 1 ? 's' : ''}?\n\nThey will be locked out immediately. You can restore them later.`)) return;
    setBulkBusy(true);
    try {
      const res = await apiCall('/api/admin/users/bulk-archive', {
        method: 'POST',
        body: JSON.stringify({ user_ids: ids }),
      });
      toast.success(res.message || `Archived ${res.archived} user(s)`);
      setAllUsers(arr => arr.map(u =>
        ids.includes(u._id) && u.role !== 'admin'
          ? { ...u, is_archived: true, is_active: false }
          : u
      ));
      clearSelection();
    } catch (e) {
      toast.error(e.message || 'Bulk archive failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const startBulkDelete = () => {
    const ids = Array.from(selectedUserIds);
    if (!ids.length) return;
    setBulkDeleteConfirm({ count: ids.length, typed: '' });
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedUserIds);
    const expected = `DELETE-${ids.length}-USERS`;
    if ((bulkDeleteConfirm?.typed || '').trim() !== expected) {
      toast.error(`Type "${expected}" exactly to confirm.`);
      return;
    }
    setBulkBusy(true);
    try {
      const res = await apiCall('/api/admin/users/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ user_ids: ids, confirm: expected }),
      });
      toast.success(res.message || `Deleted ${res.deleted} user(s)`);
      setAllUsers(arr => arr.filter(u => !ids.includes(u._id) || u.role === 'admin'));
      clearSelection();
      setBulkDeleteConfirm(null);
    } catch (e) {
      toast.error(e.message || 'Bulk delete failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const tabs = [
    { id: 'stats', label: 'Dashboard', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'command-center', label: 'Command Center', icon: <Activity className="w-4 h-4" /> },
    { id: 'config-center', label: 'Configuration', icon: <Settings className="w-4 h-4" /> },
    { id: 'security-monitor', label: 'Security Monitor', icon: <Shield className="w-4 h-4" /> },
    { id: 'finance-hq', label: 'Finance HQ', icon: <DollarSign className="w-4 h-4" /> },
    { id: 'settlements', label: 'Settlements', icon: <DollarSign className="w-4 h-4" /> },
    { id: 'profile-verification', label: 'Profile Verification', icon: <Shield className="w-4 h-4" /> },
    { id: 'background-checks', label: 'Background Checks', icon: <ShieldCheck className="w-4 h-4" /> },
    { id: 'payment-disputes', label: 'Payment Disputes', icon: <DollarSign className="w-4 h-4" /> },
    { id: 'verification', label: `Verification (${pendingUsers.length})`, icon: <UserCheck className="w-4 h-4" /> },
    { id: 'certificates', label: `Certificates (${pendingCertificates.length})`, icon: <FileCheck className="w-4 h-4" /> },
    { id: 'tickets', label: `Support (${ticketStats?.open || 0})`, icon: <Headphones className="w-4 h-4" /> },
    { id: 'users', label: 'All Users', icon: <Users className="w-4 h-4" /> },
    { id: 'user-mgmt', label: 'User Mgmt', icon: <UserPlus className="w-4 h-4" /> },
    { id: 'jobs', label: 'Jobs/Shifts', icon: <Briefcase className="w-4 h-4" /> },
    { id: 'audit', label: 'Audit Trail', icon: <Activity className="w-4 h-4" /> },
    { id: 'marketplace', label: 'Marketplace', icon: <Store className="w-4 h-4" /> },
    { id: 'reports', label: 'Report Settings', icon: <AlertTriangle className="w-4 h-4" /> },
    { id: 'support', label: 'Support Mode', icon: <Shield className="w-4 h-4" /> },
    { id: 'commission', label: 'Commission', icon: <Percent className="w-4 h-4" /> },
    { id: 'min-pay', label: 'Minimum Pay', icon: <DollarSign className="w-4 h-4" /> },
    { id: 'spin-rewards', label: 'Spin Rewards', icon: <Sparkles className="w-4 h-4" /> },
    { id: 'streak-rewards', label: 'Streak Rewards', icon: <Sparkles className="w-4 h-4" /> },
    { id: 'data-quality', label: 'Data Quality', icon: <Activity className="w-4 h-4" /> },
    { id: 'security', label: 'Security', icon: <Shield className="w-4 h-4" /> },
    { id: 'hris', label: 'HRIS', icon: <FileText className="w-4 h-4" /> },
    { id: 'help-training', label: 'Help & Training', icon: <BookOpen className="w-4 h-4" /> },
  ];

  const ticketFilters = [
    { id: 'all', label: 'All', count: supportTickets.length },
    { id: 'open', label: 'Open', count: supportTickets.filter(t => t.status === 'open').length },
    { id: 'in_progress', label: 'In Progress', count: supportTickets.filter(t => t.status === 'in_progress').length },
    { id: 'resolved', label: 'Resolved', count: supportTickets.filter(t => t.status === 'resolved').length },
    { id: 'closed', label: 'Closed', count: supportTickets.filter(t => t.status === 'closed').length },
  ];

  const getFilteredTickets = () => {
    if (ticketFilter === 'all') return supportTickets;
    return supportTickets.filter(t => t.status === ticketFilter);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-700';
      case 'in_progress': return 'bg-amber-100 text-amber-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const userFilters = [
    { id: 'all', label: 'All Users', count: allUsers.length },
    { id: 'worker', label: 'Workers', count: allUsers.filter(u => u.role === 'worker').length },
    { id: 'employer', label: 'Employers', count: allUsers.filter(u => u.role === 'employer').length },
    { id: 'agent', label: 'Agents', count: allUsers.filter(u => u.role === 'agent').length },
  ];

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'worker': return 'bg-blue-100 text-blue-700';
      case 'employer': return 'bg-purple-100 text-purple-700';
      case 'agent': return 'bg-teal-100 text-teal-700';
      case 'admin': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusBadge = (u) => {
    if (u.auto_lockout_at) return 'bg-red-100 text-red-800';
    if (u.is_archived) return 'bg-amber-100 text-amber-800';
    const isVerified = u.is_admin_verified || u.role === 'worker' || u.role === 'admin';
    return isVerified
      ? 'bg-green-100 text-green-700'
      : 'bg-yellow-100 text-yellow-700';
  };

  const getStatusText = (u) => {
    if (u.auto_lockout_at) return '🔒 Auto-locked';
    if (u.is_archived) return 'Archived';
    return (u.is_admin_verified || u.role === 'worker' || u.role === 'admin') ? 'Verified' : 'Pending';
  };

  // ---------- Archive / Delete (super-admin) ----------
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { user, typed }
  const [activityDrawerUserId, setActivityDrawerUserId] = useState(null);
  const [busyUserId, setBusyUserId] = useState(null);

  const adminArchiveUser = async (u) => {
    if (!window.confirm(`Archive ${u.full_name || u.email}?\n\nThey will be locked out of ScensobConnect immediately. You can restore them later.`)) return;
    setBusyUserId(u._id);
    try {
      await apiCall(`/api/admin/users/${u._id}/archive`, { method: 'POST' });
      toast.success('User archived');
      setAllUsers((arr) => arr.map((x) => (x._id === u._id ? { ...x, is_archived: true, is_active: false } : x)));
    } catch (e) {
      toast.error(e.message || 'Could not archive user');
    } finally {
      setBusyUserId(null);
    }
  };

  const adminUnarchiveUser = async (u) => {
    setBusyUserId(u._id);
    try {
      await apiCall(`/api/admin/users/${u._id}/unarchive`, { method: 'POST' });
      toast.success('User restored');
      setAllUsers((arr) => arr.map((x) => (x._id === u._id ? { ...x, is_archived: false, is_active: true } : x)));
    } catch (e) {
      toast.error(e.message || 'Could not restore user');
    } finally {
      setBusyUserId(null);
    }
  };

  const adminDeleteUser = async () => {
    const { user: u, typed } = deleteConfirm || {};
    if (!u || (typed || '').trim().toLowerCase() !== (u.email || '').trim().toLowerCase()) {
      toast.error("Type the user's exact email to confirm.");
      return;
    }
    setBusyUserId(u._id);
    try {
      await apiCall(`/api/admin/users/${u._id}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirm_email: u.email }),
      });
      toast.success(`${u.email} permanently deleted`);
      setAllUsers((arr) => arr.filter((x) => x._id !== u._id));
      setDeleteConfirm(null);
    } catch (e) {
      toast.error(e.message || 'Could not delete user');
    } finally {
      setBusyUserId(null);
    }
  };
  const handleAdminPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose a JPEG, PNG, or WebP image');
      e.target.value = '';
      return;
    }
    try {
      let toSend = file;
      try {
        toSend = await compressImage(file);
      } catch (_err) {
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('This image format could not be processed — please use a JPG or PNG under 5 MB');
        }
      }
      const form = new FormData();
      form.append('file', toSend);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employer/profile/photo`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Upload failed');
      updateUser({ ...user, profile_photo: data.file_url, profile_image: data.file_url });
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.message || 'Could not upload photo');
    } finally {
      if (e.target) e.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* First-login training card — self-dismisses once seen */}
      <OnboardingCard role="admin" />
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 text-white">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <ScensobConnectLogo size={40} subtitle="Admin Portal" />
            <div className="flex items-center gap-4">
              {/* Admin profile photo with upload */}
              <label className="relative cursor-pointer group" title="Upload profile photo" data-testid="admin-photo-upload-label">
                {user?.profile_photo || user?.profile_image ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_photo || user.profile_image}`}
                    alt="Profile"
                    className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center border-2 border-white/30">
                    <span className="text-sm font-semibold">{(user?.full_name || 'A').charAt(0)}</span>
                  </div>
                )}
                <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-cyan-500 text-white flex items-center justify-center text-[9px] group-hover:bg-cyan-400">✎</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleAdminPhotoUpload}
                  data-testid="admin-photo-upload-input"
                />
              </label>
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-white/70">Administrator</p>
              </div>
              <RoleSwitcher tone="dark" />
              <button 
                onClick={logout} 
                className="p-2.5 hover:bg-white/10 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-1 pb-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-2.5 xl:px-3.5 py-3 rounded-t-lg transition-all whitespace-nowrap text-sm ${
                  activeTab === tab.id
                    ? 'bg-slate-50 text-slate-900 font-medium'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                {tab.icon}
                {tab.label}
                {tab.id === 'settlements' && settleAttn > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="settlements-attention-badge">{settleAttn}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {/* Stats/Dashboard Tab */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            {/* Welcome Banner */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-1">Platform Overview</h2>
                  <p className="text-white/80">Monitor platform activity and manage users</p>
                </div>
                <button
                  onClick={() => { loadStats(); loadPendingUsers(); }}
                  className="p-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* KPI Cards */}
            {stats ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div 
                    className="bg-white rounded-2xl p-5 border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => { setActiveTab('users'); setUserFilter('all'); }}
                    data-testid="stats-total-users"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl text-white">
                        <Users className="w-5 h-5" />
                      </div>
                      <span className="text-xs text-green-600 font-medium">+12%</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_users}</p>
                    <p className="text-sm text-gray-500">Total Users</p>
                  </div>

                  <div 
                    className="bg-white rounded-2xl p-5 border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => { setActiveTab('users'); setUserFilter('worker'); }}
                    data-testid="stats-workers"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl text-white">
                        <Users className="w-5 h-5" />
                      </div>
                      <span className="text-xs text-green-600 font-medium">+8%</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_workers}</p>
                    <p className="text-sm text-gray-500">Workers</p>
                  </div>

                  <div 
                    className="bg-white rounded-2xl p-5 border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => { setActiveTab('users'); setUserFilter('employer'); }}
                    data-testid="stats-employers"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl text-white">
                        <Building2 className="w-5 h-5" />
                      </div>
                      <span className="text-xs text-green-600 font-medium">+5%</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_employers}</p>
                    <p className="text-sm text-gray-500">Employers</p>
                  </div>

                  <div 
                    className="bg-white rounded-2xl p-5 border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => { setActiveTab('users'); setUserFilter('agent'); }}
                    data-testid="stats-agents"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl text-white">
                        <Shield className="w-5 h-5" />
                      </div>
                      <span className="text-xs text-green-600 font-medium">+3%</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_agents}</p>
                    <p className="text-sm text-gray-500">Agents</p>
                  </div>
                </div>

                {/* Job Stats */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-2xl p-5 border border-gray-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Briefcase className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="font-medium text-gray-900">Total Jobs</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_jobs}</p>
                  </div>

                  <div className="bg-white rounded-2xl p-5 border border-gray-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <Clock className="w-5 h-5 text-yellow-600" />
                      </div>
                      <span className="font-medium text-gray-900">Active Jobs</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.active_jobs}</p>
                  </div>

                  <div className="bg-white rounded-2xl p-5 border border-gray-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="font-medium text-gray-900">Completed Jobs</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.completed_jobs}</p>
                  </div>
                </div>

                {/* Alerts */}
                {stats.pending_verifications > 0 && (
                  <div 
                    className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between cursor-pointer hover:shadow-md transition"
                    onClick={() => setActiveTab('verification')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-amber-900">
                          {stats.pending_verifications} users pending verification
                        </p>
                        <p className="text-sm text-amber-700">Click to review and approve</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-amber-600" />
                  </div>
                )}

                {/* iter81: Recent Auto-Lockouts widget (super-admin visibility) */}
                {user?.is_super_admin && (
                  <RecentAutoLockoutsCard
                    onViewUser={(uid) => {
                      setActiveTab('user-mgmt');
                      // UserManagementPanel picks up the current user for drilldown;
                      // if a router integration is added later, deep-link here.
                    }}
                  />
                )}

                {/* Feb 2026: Data Quality snapshot card */}
                <DataQualityOverviewCard
                  onOpen={() => setActiveTab('data-quality')}
                />

                {/* Jul 2026: Financial OS platform snapshot */}
                <PlatformFinanceCard />

                {/* Jul 2026: Financial OS Phase 3 — configurable revenue rules */}
                <RevenueRulesPanel />

                {/* Jul 2026: Financial OS Phase 3 leftovers — plans + coupons */}
                <MonetizationPanel />
              </>
            ) : (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
              </div>
            )}
          </div>
        )}

        {/* Verification Tab */}
        {activeTab === 'verification' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                  <UserCheck className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">User Verification Queue</h2>
                  <p className="text-white/80">Review and approve new user registrations</p>
                </div>
              </div>
            </div>
            
            {pendingUsers.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 text-center py-16">
                <div className="p-4 bg-green-100 rounded-full w-fit mx-auto mb-4">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">All Caught Up!</h3>
                <p className="text-gray-600">No users pending verification</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingUsers.map((pendingUser) => (
                  <div 
                    key={pendingUser._id} 
                    className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
                    data-testid="verification-row"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {pendingUser.full_name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {pendingUser.full_name}
                          </h3>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p className="flex items-center gap-2">
                              <span className="text-gray-400">Email:</span> 
                              {pendingUser.email}
                            </p>
                            <p className="flex items-center gap-2">
                              <span className="text-gray-400">Phone:</span> 
                              {pendingUser.phone || 'Not provided'}
                            </p>
                            <p className="flex items-center gap-2">
                              <span className="text-gray-400">Role:</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(pendingUser.role)}`}>
                                {pendingUser.role}
                              </span>
                            </p>
                            <p className="flex items-center gap-2 text-xs text-gray-400">
                              <Clock className="w-3 h-3" />
                              Registered: {new Date(pendingUser.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewUser(pendingUser)}
                          className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all"
                          data-testid="view-user-button"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        <button
                          onClick={() => handleVerifyUser(pendingUser._id, 'approve')}
                          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all"
                          data-testid="verify-approve-button"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleVerifyUser(pendingUser._id, 'reject')}
                          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all"
                          data-testid="verify-reject-button"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Certificate Verification Tab */}
        {activeTab === 'certificates' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <FileCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Certificate Verification</h2>
                    <p className="text-white/80">{pendingCertificates.length} certificates awaiting review</p>
                  </div>
                </div>
                <button
                  onClick={loadPendingCertificates}
                  className="p-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition"
                  disabled={certLoading}
                  data-testid="refresh-certificates-btn"
                >
                  <RefreshCw className={`w-5 h-5 ${certLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {certLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
              </div>
            ) : pendingCertificates.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
                <div className="p-4 bg-emerald-100 rounded-full w-fit mx-auto mb-4">
                  <FileCheck className="w-12 h-12 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">All Caught Up!</h3>
                <p className="text-gray-600">No certificates pending verification at the moment.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingCertificates.map((cert) => (
                  <div 
                    key={cert._id} 
                    className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition"
                    data-testid={`cert-card-${cert._id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl">
                          <Award className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{cert.certificate_name}</h3>
                          {cert.issuing_organization && (
                            <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                              <Building2 className="w-4 h-4" />
                              {cert.issuing_organization}
                            </p>
                          )}
                          
                          {/* Worker Info */}
                          {cert.worker_info && (
                            <div className="flex items-center gap-2 mt-2 p-2 bg-gray-50 rounded-lg">
                              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {cert.worker_info.full_name?.charAt(0).toUpperCase() || 'W'}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{cert.worker_info.full_name}</p>
                                <p className="text-xs text-gray-500">{cert.worker_info.email}</p>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex flex-wrap gap-3 mt-3 text-sm text-gray-500">
                            {cert.certificate_number && (
                              <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-lg">
                                <FileText className="w-3.5 h-3.5" />
                                #{cert.certificate_number}
                              </span>
                            )}
                            {cert.issue_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                Issued: {new Date(cert.issue_date).toLocaleDateString()}
                              </span>
                            )}
                            {cert.expiry_date && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                Expires: {new Date(cert.expiry_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          
                          {/* Document Link */}
                          {cert.file_url && (
                            <SecureFileLink path={cert.file_url} workerId={cert.worker_id} fileLabel={cert.certificate_name}
                              className="inline-flex items-center gap-1 mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                              testId={`view-cert-doc-${cert._id}`}>
                              <ExternalLink className="w-4 h-4" /> View Certificate Document
                            </SecureFileLink>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium text-center">
                          Pending Manual Review
                        </span>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleVerifyCertificate(cert._id, 'approve')}
                            disabled={verifyingCert === cert._id}
                            className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl text-sm font-medium hover:shadow-lg transition disabled:opacity-50"
                            data-testid={`approve-cert-${cert._id}`}
                          >
                            {verifyingCert === cert._id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => handleVerifyCertificate(cert._id, 'reject', 'Certificate rejected by admin')}
                            disabled={verifyingCert === cert._id}
                            className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl text-sm font-medium hover:shadow-lg transition disabled:opacity-50"
                            data-testid={`reject-cert-${cert._id}`}
                          >
                            <XCircle className="w-4 h-4" />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* All Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">User Management</h2>
                    <p className="text-white/80">{getFilteredUsers().length} users displayed</p>
                  </div>
                </div>
                <button
                  onClick={loadAllUsers}
                  className="p-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Role Filter Tabs */}
            <div className="flex gap-2 flex-wrap">
              {userFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => { setUserFilter(filter.id); clearSelection(); }}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                    userFilter === filter.id
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-md'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid={`filter-${filter.id}`}
                >
                  {filter.label}
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    userFilter === filter.id
                      ? 'bg-white/20'
                      : 'bg-gray-100'
                  }`}>
                    {filter.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Search + Status filter row */}
            <div className="flex gap-3 flex-wrap items-center">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={e => { setUserSearch(e.target.value); clearSelection(); }}
                  placeholder="Search by name, email, or phone…"
                  className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 text-gray-900 placeholder:text-gray-400"
                  data-testid="admin-users-search"
                />
              </div>
              <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'verified', label: 'Verified' },
                  { id: 'pending', label: 'Pending' },
                  { id: 'archived', label: 'Archived' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => { setUserStatusFilter(opt.id); clearSelection(); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                      userStatusFilter === opt.id
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`admin-users-status-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Floating bulk-action bar */}
            {selectedUserIds.size > 0 && (
              <div className="sticky top-2 z-30 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-xl px-4 py-3 flex items-center gap-3" data-testid="admin-bulk-action-bar">
                <span className="font-bold">{selectedUserIds.size} selected</span>
                <span className="text-white/70 text-sm flex-1">— take action on all of them</span>
                <button
                  onClick={bulkArchive}
                  disabled={bulkBusy}
                  className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="admin-bulk-archive-btn"
                >
                  <Archive className="w-4 h-4" /> Archive Selected
                </button>
                <button
                  onClick={startBulkDelete}
                  disabled={bulkBusy}
                  className="px-3 py-1.5 bg-rose-500 hover:bg-rose-600 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="admin-bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4" /> Delete Selected
                </button>
                <button
                  onClick={clearSelection}
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm"
                  data-testid="admin-bulk-clear-btn"
                >
                  Clear
                </button>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-4 w-10">
                        <input
                          type="checkbox"
                          checked={(() => {
                            const visible = getFilteredUsers().filter(u => u.role !== 'admin');
                            return visible.length > 0 && visible.every(u => selectedUserIds.has(u._id));
                          })()}
                          onChange={toggleSelectAllVisible}
                          className="w-4 h-4 accent-blue-600 cursor-pointer"
                          title="Select all on this view"
                          data-testid="admin-users-select-all"
                        />
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">User</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Email</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Role</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Joined</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {getFilteredUsers().map((u) => (
                      <tr
                        key={u._id}
                        className={`hover:bg-gray-50 transition cursor-pointer ${selectedUserIds.has(u._id) ? 'bg-blue-50/40' : ''}`}
                        onClick={() => handleViewUser(u)}
                        data-testid={`user-row-${u._id}`}
                      >
                        <td className="px-3 py-4" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedUserIds.has(u._id)}
                            onChange={() => toggleSelect(u._id)}
                            disabled={u.role === 'admin'}
                            className="w-4 h-4 accent-blue-600 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                            title={u.role === 'admin' ? 'Cannot select admins' : 'Select user'}
                            data-testid={`select-user-${u._id}`}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-medium">
                                {u.full_name?.charAt(0).toUpperCase() || 'U'}
                              </div>
                              {u.suspicious_24h >= 5 && (
                                <span
                                  className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full ring-2 ring-white"
                                  data-testid={`suspicious-dot-${u._id}`}
                                  title={`${u.suspicious_24h} failed auth attempts in the last 24h`}
                                />
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-900">{u.full_name}</span>
                              {u.suspicious_24h >= 5 && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border bg-red-50 text-red-700 border-red-200 mt-0.5 w-fit"
                                  data-testid={`suspicious-badge-${u._id}`}
                                  title={`${u.suspicious_24h} failed authentication attempts in the last 24h. Investigate.`}
                                >
                                  ⚠ {u.suspicious_24h} fails / 24h
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{u.email}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(u.role)}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(u)}`}>
                            {getStatusText(u)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-500 text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => setActivityDrawerUserId(u._id)}
                              className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg transition"
                              title="View activity timeline"
                              data-testid={`activity-button-${u._id}`}
                            >
                              <Activity className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleViewUser(u)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="View user"
                              data-testid={`view-button-${u._id}`}
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            {u.is_archived ? (
                              <button
                                onClick={() => adminUnarchiveUser(u)}
                                disabled={busyUserId === u._id}
                                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition disabled:opacity-50"
                                title="Restore user"
                                data-testid={`unarchive-button-${u._id}`}
                              >
                                <ArchiveRestore className="w-5 h-5" />
                              </button>
                            ) : (
                              u.role !== 'admin' && (
                                <button
                                  onClick={() => adminArchiveUser(u)}
                                  disabled={busyUserId === u._id}
                                  className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition disabled:opacity-50"
                                  title="Archive user (locks login)"
                                  data-testid={`archive-button-${u._id}`}
                                >
                                  <Archive className="w-5 h-5" />
                                </button>
                              )
                            )}
                            {u.role !== 'admin' && (
                              <button
                                onClick={() => setDeleteConfirm({ user: u, typed: '' })}
                                disabled={busyUserId === u._id}
                                className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition disabled:opacity-50"
                                title="Delete permanently"
                                data-testid={`delete-button-${u._id}`}
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Commission Tab */}
        {activeTab === 'commission' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                  <DollarSign className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Commission Settings</h2>
                  <p className="text-white/80">Configure platform fees and commissions</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl border border-gray-200 p-6 max-w-2xl">
              <form onSubmit={handleUpdateCommission} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Worker Fee (%)
                  </label>
                  <input
                    type="number"
                    value={commission.worker_pct}
                    onChange={(e) =>
                      setCommission({ ...commission, worker_pct: parseFloat(e.target.value) })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    required
                    min="0"
                    max="100"
                    step="0.1"
                    data-testid="commission-worker-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Percentage deducted from worker's payment
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Employer Fee (%)
                  </label>
                  <input
                    type="number"
                    value={commission.employer_pct}
                    onChange={(e) =>
                      setCommission({ ...commission, employer_pct: parseFloat(e.target.value) })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    required
                    min="0"
                    max="100"
                    step="0.1"
                    data-testid="commission-employer-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Percentage added to employer's payment
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Platform Fixed Fee ($)
                  </label>
                  <input
                    type="number"
                    value={commission.platform_fixed}
                    onChange={(e) =>
                      setCommission({ ...commission, platform_fixed: parseFloat(e.target.value) })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    required
                    min="0"
                    step="0.01"
                    data-testid="commission-fixed-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Fixed fee per transaction
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Example Calculation</h4>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-5 rounded-xl space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Job Payment:</span>
                      <span className="font-semibold text-gray-900">$100.00</span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>Worker Fee ({commission.worker_pct}%):</span>
                      <span className="font-medium">
                        -${(100 * (commission.worker_pct / 100)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>Platform Fee:</span>
                      <span className="font-medium">-${commission.platform_fixed.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600 font-semibold pt-3 border-t border-gray-300">
                      <span>Worker Receives:</span>
                      <span>
                        $
                        {(
                          100 -
                          100 * (commission.worker_pct / 100) -
                          commission.platform_fixed
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:shadow-lg font-semibold transition-all"
                  data-testid="commission-save-button"
                >
                  Save Commission Settings
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Marketplace Tab */}
        {activeTab === 'marketplace' && <AdminMarketplacePanel />}

        {activeTab === 'min-pay' && <MinPaySettingsPanel />}

        {activeTab === 'spin-rewards' && <SpinRewardsStudio />}
        {activeTab === 'streak-rewards' && <StreakRewardsPanel role="admin" />}

        {activeTab === 'user-mgmt' && <UserManagementPanel mode="admin" currentUser={user} />}

        {activeTab === 'jobs' && <AdminJobsPanel />}

        {activeTab === 'data-quality' && <DataQualityPanel />}

        {activeTab === 'payment-disputes' && <PaymentDisputes />}
        {activeTab === 'finance-hq' && <FinancialCommandCenter />}
        {activeTab === 'settlements' && <SettlementsPanel />}
        {activeTab === 'command-center' && <EnterpriseCommandCenter />}
        {activeTab === 'config-center' && <ConfigurationCenter />}
        {activeTab === 'security-monitor' && <SecurityMonitorPanel />}
        {activeTab === 'help-training' && <HelpTraining role="admin" />}

        {activeTab === 'security' && (
          <div className="space-y-6" data-testid="admin-security-tab">
            <SecurityConfigPanel />
            <SecurityCenter />
          </div>
        )}

        {activeTab === 'audit' && <AuditTrailPanel />}

        {/* Profile Verification Tab (new verification gate) */}
        {activeTab === 'profile-verification' && <AdminProfileVerificationPanel />}
        {activeTab === 'background-checks' && <BackgroundChecks />}

        {/* Report Settings Tab */}
        {activeTab === 'reports' && <AdminReportRecipientsPanel />}

        {/* Support Mode Tab */}
        {activeTab === 'support' && <AdminSupportModePanel />}
        {activeTab === 'hris' && <OrgHrisConsole adminMode />}

        {/* Support Tickets Tab */}
        {activeTab === 'tickets' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <Headphones className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Support Tickets</h2>
                    <p className="text-white/80">Manage user support requests</p>
                  </div>
                </div>
                <button
                  onClick={() => { loadSupportTickets(); loadTicketStats(); }}
                  className="p-2.5 hover:bg-white/20 rounded-xl transition"
                  title="Refresh"
                >
                  <RefreshCw className={`w-5 h-5 ${ticketLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            {ticketStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{ticketStats.open}</p>
                      <p className="text-xs text-slate-500">Open</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{ticketStats.in_progress}</p>
                      <p className="text-xs text-slate-500">In Progress</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{ticketStats.resolved}</p>
                      <p className="text-xs text-slate-500">Resolved</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{ticketStats.priorities?.high || 0}</p>
                      <p className="text-xs text-slate-500">High Priority</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <FileText className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{ticketStats.total}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filter Tabs */}
            <div className="flex gap-2 flex-wrap">
              {ticketFilters.map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setTicketFilter(filter.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                    ticketFilter === filter.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`ticket-filter-${filter.id}`}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>

            {/* Tickets Grid */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Tickets List */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
                  <h3 className="font-semibold text-slate-800">Tickets ({getFilteredTickets().length})</h3>
                </div>
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                  {ticketLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
                    </div>
                  ) : getFilteredTickets().length > 0 ? (
                    getFilteredTickets().map(ticket => (
                      <button
                        key={ticket._id}
                        onClick={() => loadTicketDetail(ticket._id)}
                        className={`w-full p-4 text-left hover:bg-slate-50 transition ${
                          selectedTicket?._id === ticket._id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''
                        }`}
                        data-testid={`ticket-item-${ticket._id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="font-medium text-slate-800 line-clamp-1">{ticket.subject}</p>
                            <p className="text-sm text-slate-500">{ticket.user_name} ({ticket.user_role})</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority || 'medium'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(ticket.status)}`}>
                            {ticket.status?.replace('_', ' ')}
                          </span>
                          <span className="text-xs text-slate-400">
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-12 text-center">
                      <Headphones className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No tickets found</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Ticket Detail */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {selectedTicket ? (
                  <>
                    <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-slate-800">{selectedTicket.subject}</h3>
                          <p className="text-sm text-slate-500 mt-1">
                            From: {selectedTicket.user_name} ({selectedTicket.user_email})
                          </p>
                        </div>
                        <button
                          onClick={() => setSelectedTicket(null)}
                          className="p-1 hover:bg-slate-200 rounded"
                        >
                          <X className="w-4 h-4 text-slate-500" />
                        </button>
                      </div>
                      {/* Status & Priority Controls */}
                      <div className="flex gap-3 mt-3">
                        <select
                          value={selectedTicket.status}
                          onChange={(e) => updateTicketStatus(selectedTicket._id, e.target.value)}
                          className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg bg-white text-slate-700"
                          data-testid="ticket-status-select"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                        <select
                          value={selectedTicket.priority || 'medium'}
                          onChange={(e) => updateTicketPriority(selectedTicket._id, e.target.value)}
                          className={`px-3 py-1.5 text-sm border rounded-lg ${getPriorityColor(selectedTicket.priority)}`}
                          data-testid="ticket-priority-select"
                        >
                          <option value="high">High Priority</option>
                          <option value="medium">Medium Priority</option>
                          <option value="low">Low Priority</option>
                        </select>
                      </div>
                    </div>

                    <div className="p-4 max-h-[400px] overflow-y-auto space-y-4">
                      {/* Original Description */}
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-1">Description</p>
                        <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg">{selectedTicket.description}</p>
                      </div>

                      {/* Conversation History */}
                      {selectedTicket.conversation_history?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-2">AI Chat History</p>
                          <div className="bg-slate-50 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                            {selectedTicket.conversation_history.map((msg, idx) => (
                              <div key={`msg-${msg.timestamp || idx}`} className={`text-sm ${msg.role === 'user' ? 'text-slate-800' : 'text-slate-600 italic'}`}>
                                <span className="font-medium">{msg.role === 'user' ? 'User:' : 'AI:'}</span> {msg.content?.substring(0, 200)}{msg.content?.length > 200 ? '...' : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Admin Responses */}
                      {selectedTicket.admin_responses?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-2">Admin Responses</p>
                          <div className="space-y-2">
                            {selectedTicket.admin_responses.map((resp, idx) => (
                              <div key={`resp-${resp.timestamp || idx}`} className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-indigo-700">{resp.admin_name}</span>
                                  <span className="text-xs text-indigo-500">{new Date(resp.timestamp).toLocaleString()}</span>
                                </div>
                                <p className="text-sm text-slate-700">{resp.message}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Internal Notes (Admin Only) */}
                      {selectedTicket.internal_notes?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-amber-600 mb-2">Internal Notes (Hidden from user)</p>
                          <div className="space-y-2">
                            {selectedTicket.internal_notes.map((note, idx) => (
                              <div key={`note-${note.timestamp || idx}`} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-amber-700">{note.admin_name}</span>
                                  <span className="text-xs text-amber-500">{new Date(note.timestamp).toLocaleString()}</span>
                                </div>
                                <p className="text-sm text-slate-700">{note.note}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Panel */}
                    <div className="p-4 border-t border-slate-200 space-y-3">
                      {/* Send Response to User */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Respond to User</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={respondText}
                            onChange={(e) => setRespondText(e.target.value)}
                            placeholder="Type your response..."
                            className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg text-slate-800"
                            data-testid="ticket-respond-input"
                          />
                          <button
                            onClick={sendTicketResponse}
                            disabled={sendingResponse || !respondText.trim()}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition flex items-center gap-2"
                            data-testid="ticket-respond-btn"
                          >
                            {sendingResponse ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Send
                          </button>
                        </div>
                      </div>

                      {/* Add Internal Note */}
                      <div>
                        <label className="text-xs font-medium text-amber-600 mb-1 block">Add Internal Note</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Internal note (not visible to user)..."
                            className="flex-1 px-3 py-2 text-sm border border-amber-300 rounded-lg text-slate-800 bg-amber-50"
                            data-testid="ticket-note-input"
                          />
                          <button
                            onClick={addInternalNote}
                            disabled={addingNote || !noteText.trim()}
                            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition"
                            data-testid="ticket-note-btn"
                          >
                            {addingNote ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Add Note'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">Select a ticket to view details</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className={`p-6 text-white ${
              selectedUser.role === 'worker' ? 'bg-gradient-to-r from-blue-600 to-cyan-600' :
              selectedUser.role === 'employer' ? 'bg-gradient-to-r from-purple-600 to-pink-600' :
              selectedUser.role === 'agent' ? 'bg-gradient-to-r from-teal-600 to-emerald-600' :
              'bg-gradient-to-r from-gray-600 to-gray-700'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-2xl font-bold">
                    {selectedUser.full_name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{selectedUser.full_name}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-3 py-1 bg-white/20 rounded-full text-sm capitalize">
                        {selectedUser.role}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        (selectedUser.is_admin_verified || selectedUser.role === 'worker' || selectedUser.role === 'admin')
                          ? 'bg-green-500/30'
                          : 'bg-yellow-500/30'
                      }`}>
                        {getStatusText(selectedUser)}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-2 hover:bg-white/20 rounded-lg transition"
                  data-testid="close-modal-button"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {userDetailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Contact Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="font-medium text-gray-900">{selectedUser.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Phone className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="font-medium text-gray-900">{selectedUser.phone || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <MapPin className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Location</p>
                          <p className="font-medium text-gray-900">{selectedUser.address || selectedUser.location?.city || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Calendar className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Joined</p>
                          <p className="font-medium text-gray-900">
                            {new Date(selectedUser.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Role-specific Stats */}
                  {userDetail?.stats && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        {selectedUser.role === 'worker' ? 'Worker Statistics' :
                         selectedUser.role === 'employer' ? 'Employer Statistics' :
                         selectedUser.role === 'agent' ? 'Agent Statistics' : 'Statistics'}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {selectedUser.role === 'worker' && (
                          <>
                            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-xl border border-blue-100">
                              <div className="flex items-center gap-2 mb-2">
                                <Briefcase className="w-4 h-4 text-blue-600" />
                                <span className="text-xs text-gray-500">Jobs Completed</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">{userDetail.stats.jobs_completed}</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100">
                              <div className="flex items-center gap-2 mb-2">
                                <Activity className="w-4 h-4 text-green-600" />
                                <span className="text-xs text-gray-500">Active Jobs</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">{userDetail.stats.jobs_active}</p>
                            </div>
                            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl border border-amber-100">
                              <div className="flex items-center gap-2 mb-2">
                                <Star className="w-4 h-4 text-amber-600" />
                                <span className="text-xs text-gray-500">Rating</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">{userDetail.stats.rating?.toFixed(1) || '0.0'}</p>
                            </div>
                            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-100">
                              <div className="flex items-center gap-2 mb-2">
                                <FileText className="w-4 h-4 text-purple-600" />
                                <span className="text-xs text-gray-500">Total Applied</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">{userDetail.stats.total_applied}</p>
                            </div>
                            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-4 rounded-xl border border-teal-100 col-span-2">
                              <div className="flex items-center gap-2 mb-2">
                                <Wallet className="w-4 h-4 text-teal-600" />
                                <span className="text-xs text-gray-500">Total Earnings</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">${userDetail.stats.total_earnings?.toFixed(2) || '0.00'}</p>
                            </div>
                          </>
                        )}
                        {selectedUser.role === 'employer' && (
                          <>
                            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-100">
                              <div className="flex items-center gap-2 mb-2">
                                <Briefcase className="w-4 h-4 text-purple-600" />
                                <span className="text-xs text-gray-500">Jobs Posted</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">{userDetail.stats.jobs_posted}</p>
                            </div>
                            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-4 rounded-xl border border-yellow-100">
                              <div className="flex items-center gap-2 mb-2">
                                <Clock className="w-4 h-4 text-yellow-600" />
                                <span className="text-xs text-gray-500">Active Jobs</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">{userDetail.stats.jobs_active}</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100">
                              <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span className="text-xs text-gray-500">Completed</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">{userDetail.stats.jobs_completed}</p>
                            </div>
                            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-4 rounded-xl border border-teal-100 col-span-3">
                              <div className="flex items-center gap-2 mb-2">
                                <DollarSign className="w-4 h-4 text-teal-600" />
                                <span className="text-xs text-gray-500">Total Spent</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">${userDetail.stats.total_spent?.toFixed(2) || '0.00'}</p>
                            </div>
                          </>
                        )}
                        {selectedUser.role === 'agent' && (
                          <>
                            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-4 rounded-xl border border-teal-100">
                              <div className="flex items-center gap-2 mb-2">
                                <Users className="w-4 h-4 text-teal-600" />
                                <span className="text-xs text-gray-500">Workers Managed</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">{userDetail.stats.workers_managed}</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100">
                              <div className="flex items-center gap-2 mb-2">
                                <UserCheck className="w-4 h-4 text-green-600" />
                                <span className="text-xs text-gray-500">Active Workers</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">{userDetail.stats.active_workers}</p>
                            </div>
                            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl border border-amber-100">
                              <div className="flex items-center gap-2 mb-2">
                                <DollarSign className="w-4 h-4 text-amber-600" />
                                <span className="text-xs text-gray-500">Commission Earned</span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">${userDetail.stats.commission_earned?.toFixed(2) || '0.00'}</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Skills (for workers) */}
                  {selectedUser.role === 'worker' && selectedUser.skills && selectedUser.skills.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedUser.skills.map((skill, idx) => (
                          <span key={`${skill}-${idx}`} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Company Info (for employers) */}
                  {selectedUser.role === 'employer' && selectedUser.company_name && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                      <div className="bg-gray-50 p-4 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <Building2 className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Company Name</p>
                            <p className="font-medium text-gray-900">{selectedUser.company_name}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    {!selectedUser.is_admin_verified && selectedUser.role !== 'worker' && selectedUser.role !== 'admin' && (
                      <>
                        <button
                          onClick={() => { handleVerifyUser(selectedUser._id, 'approve'); handleCloseModal(); }}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                        >
                          <CheckCircle className="w-5 h-5" />
                          Approve User
                        </button>
                        <button
                          onClick={() => { handleVerifyUser(selectedUser._id, 'reject'); handleCloseModal(); }}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                        >
                          <XCircle className="w-5 h-5" />
                          Reject User
                        </button>
                      </>
                    )}
                    <button
                      onClick={handleCloseModal}
                      className={`${(!selectedUser.is_admin_verified && selectedUser.role !== 'worker' && selectedUser.role !== 'admin') ? '' : 'flex-1'} px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all`}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Bulk-delete confirmation modal */}
      {bulkDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setBulkDeleteConfirm(null)}
          data-testid="admin-bulk-delete-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Delete {bulkDeleteConfirm.count} user{bulkDeleteConfirm.count !== 1 ? 's' : ''} permanently?
                </h3>
                <p className="text-xs text-rose-600 font-semibold">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              You&apos;re about to permanently delete <strong>{bulkDeleteConfirm.count}</strong> selected account
              {bulkDeleteConfirm.count !== 1 ? 's' : ''} and cascade-clean their messages, invites, notifications,
              and preferred-worker entries.
            </p>
            <p className="text-sm text-gray-700 mb-2 mt-3">
              To proceed, type this exact phrase:
            </p>
            <div className="mb-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm text-rose-700 font-bold tracking-wide">
              DELETE-{bulkDeleteConfirm.count}-USERS
            </div>
            <input
              type="text"
              value={bulkDeleteConfirm.typed}
              onChange={(e) => setBulkDeleteConfirm((s) => ({ ...s, typed: e.target.value }))}
              placeholder={`DELETE-${bulkDeleteConfirm.count}-USERS`}
              autoFocus
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent text-gray-900 placeholder:text-gray-400 bg-white font-mono"
              data-testid="admin-bulk-delete-confirm-input"
            />
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setBulkDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold text-sm"
                data-testid="admin-bulk-delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={
                  bulkBusy ||
                  (bulkDeleteConfirm.typed || '').trim() !== `DELETE-${bulkDeleteConfirm.count}-USERS`
                }
                className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-bulk-delete-confirm-btn"
              >
                {bulkBusy ? 'Deleting…' : `Delete ${bulkDeleteConfirm.count} user${bulkDeleteConfirm.count !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete-user confirmation modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setDeleteConfirm(null)}
          data-testid="admin-delete-user-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete user permanently?</h3>
                <p className="text-xs text-rose-600 font-semibold">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              You&apos;re about to <strong>permanently delete</strong> the account for:
            </p>
            <div className="mt-2 mb-4 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="font-semibold text-gray-900">{deleteConfirm.user.full_name}</p>
              <p className="text-sm text-gray-600">{deleteConfirm.user.email}</p>
              <p className="text-[11px] text-gray-500 mt-1">role: {deleteConfirm.user.role}</p>
            </div>
            <p className="text-sm text-gray-700 mb-2">
              To proceed, type the user&apos;s email exactly:
            </p>
            <input
              type="email"
              value={deleteConfirm.typed}
              onChange={(e) => setDeleteConfirm((s) => ({ ...s, typed: e.target.value }))}
              placeholder={deleteConfirm.user.email}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent text-gray-900 placeholder:text-gray-400 bg-white"
              data-testid="admin-delete-confirm-email-input"
            />
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold text-sm"
                data-testid="admin-delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={adminDeleteUser}
                disabled={
                  busyUserId === deleteConfirm.user._id ||
                  (deleteConfirm.typed || '').trim().toLowerCase() !==
                    (deleteConfirm.user.email || '').trim().toLowerCase()
                }
                className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-delete-confirm-btn"
              >
                {busyUserId === deleteConfirm.user._id ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-user activity drawer (with auto-lockout banner + Unlock & Restore) */}
      {activityDrawerUserId && (
        <UserActivityDrawer
          userId={activityDrawerUserId}
          onClose={() => { setActivityDrawerUserId(null); loadAllUsers(); }}
          currentUser={user}
        />
      )}
    </div>
  );
}
